import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import type { FrequencyFile } from '@wheere/api-site';
import { WhButtonComponent, WhDropzoneComponent, WhSegmentedComponent } from '@wheere-front/shared-ui';
import {
  calibCells,
  formatDelta,
  formatMHz,
  formatThetaDegrees,
  frequencyFileStats,
  GeneratedSignalView,
  isRealCalibCell,
  prettyFrequencyFile,
  prettySignalJson,
  signalJsonText,
} from '../util/frequency-file';
import {
  AntennaTagComponent,
  antennaColour,
  downloadText,
  FrequencyModalComponent,
  KvComponent,
  ThetaSparkComponent,
} from './frequency-ui.components';

/** Grille lisible : index, fréquence MHz, theta entier (+ degrés indicatifs). */
@Component({
  selector: 'lib-frequency-entries',
  standalone: true,
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-1.5">
      @for (f of shown(); track $index) {
        <div
          class="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white p-1.5 dark:border-slate-700 dark:bg-slate-800">
          <span
            class="w-7 shrink-0 rounded bg-gray-100 py-1 text-center text-[11px] font-bold tabular-nums text-gray-500 dark:bg-slate-700 dark:text-slate-400"
            >{{ $index }}</span
          >
          <span class="min-w-0 flex-1 whitespace-nowrap text-[12px] tabular-nums text-gray-800 dark:text-slate-200"
            >{{ mhz(f) }} <span class="text-[10px] text-gray-400">MHz</span></span
          >
          <span class="shrink-0 text-right text-[12px] text-gray-800 dark:text-slate-200">
            @if (theta()[$index] === undefined) {
              <span class="text-red-500">—</span>
            } @else {
              <b class="tabular-nums">{{ theta()[$index] }}</b
              ><span class="ml-1 text-[10px] text-gray-400 dark:text-slate-500">{{ deg(theta()[$index]) }}</span>
            }
          </span>
        </div>
      }
    </div>
    @if (freq().length > limit()) {
      <p class="mt-1.5 text-[11px] text-gray-400 dark:text-slate-500">
        {{ 'siteConfiguration.frequency.moreFrequencies' | transloco: { count: freq().length - limit() } }}
      </p>
    }
  `,
})
export class FrequencyEntriesComponent {
  readonly freq = input.required<readonly number[]>();
  readonly theta = input.required<readonly number[]>();
  readonly limit = input(48);
  protected readonly shown = computed(() => this.freq().slice(0, this.limit()));
  protected readonly mhz = (f: number) => formatMHz(f);
  protected readonly deg = (t: number) => formatThetaDegrees(t);
}

/** Aperçu d'un fichier de fréquences : résumé, freq_delta par antenne, courbe du theta, lisible ou JSON. */
@Component({
  selector: 'lib-frequency-file-dialog',
  standalone: true,
  imports: [
    TranslocoModule,
    WhButtonComponent,
    WhSegmentedComponent,
    FrequencyModalComponent,
    KvComponent,
    ThetaSparkComponent,
    FrequencyEntriesComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lib-frequency-modal
      *transloco="let t"
      [title]="t('siteConfiguration.frequency.fileDialogTitle', { name: fileName() })"
      (closed)="closed.emit()">
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <lib-kv [label]="t('siteConfiguration.frequency.frequencies')" [value]="stats().count" />
        <lib-kv [label]="t('siteConfiguration.frequency.rangeMHz')" [value]="range()" />
        <lib-kv label="freq_delta" [value]="deltaSummary(t)" />
        <lib-kv label="theta min" [value]="stats().thetaMin + ' (' + deg(stats().thetaMin) + ')'" />
        <lib-kv label="theta max" [value]="stats().thetaMax + ' (' + deg(stats().thetaMax) + ')'" />
      </div>
      <div class="flex flex-wrap items-center gap-1">
        @for (d of file().freq_delta; track $index) {
          <span
            class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] tabular-nums"
            [class]="
              ignored($index)
                ? 'bg-warmYellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300'
                : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-200'
            ">
            <span
              class="inline-block size-2 rounded-[3px]"
              [style.background]="ignored($index) ? '#ffc800' : colour($index)"></span>
            <b>A{{ $index + 1 }}</b> {{ delta(d) }}
            @if (ignored($index)) {
              · {{ t('siteConfiguration.frequency.ignoredOne') }}
            }
          </span>
        }
      </div>
      <div class="rounded-lg border border-gray-200 p-3 dark:border-slate-700">
        <p class="mb-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
          {{ t('siteConfiguration.frequency.thetaCurve') }}
        </p>
        <lib-theta-spark class="block h-14 w-full" [theta]="file().theta" [width]="820" [height]="56" [fill]="true" />
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[11px] text-gray-500 dark:text-slate-400">{{
          t('siteConfiguration.frequency.baseHint')
        }}</span>
        <wh-segmented
          class="ml-auto"
          size="sm"
          [scroll]="false"
          [options]="[
            { value: 'readable', label: t('siteConfiguration.frequency.readable') },
            { value: 'json', label: 'JSON' },
          ]"
          [(value)]="view" />
      </div>
      <div
        class="max-h-[44vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-slate-700 dark:bg-slate-900/40">
        @if (view() === 'readable') {
          <lib-frequency-entries
            [freq]="file().freq"
            [theta]="file().theta"
            [limit]="all() ? file().freq.length : 48" />
        } @else {
          <pre class="font-mono text-[12px] leading-5 text-gray-800 dark:text-slate-200">{{ pretty() }}</pre>
        }
      </div>
      @if (view() === 'readable' && !all() && file().freq.length > 48) {
        <div class="text-center">
          <wh-button variant="ghost" size="xs" (click)="all.set(true)">{{
            t('siteConfiguration.frequency.showAll', { count: file().freq.length })
          }}</wh-button>
        </div>
      }
      <ng-container slot="footer">
        <wh-button variant="outline" size="xs" userClass="text-blue-600 dark:text-blue-400" (click)="download()">
          <i class="pi pi-cloud-download mr-1 text-xs"></i>{{ t('siteConfiguration.files.download') }}
        </wh-button>
        <wh-button variant="outline" size="xs" (click)="closed.emit()">{{
          t('siteConfiguration.files.close')
        }}</wh-button>
      </ng-container>
    </lib-frequency-modal>
  `,
})
export class FrequencyFileDialogComponent {
  readonly fileName = input.required<string>();
  readonly file = input.required<FrequencyFile>();
  /** Antennes du site (freq_delta en surplus marqués « ignoré ») ; null pour un preset. */
  readonly antennaCount = input<number | null>(null);
  readonly closed = output<void>();

  protected readonly view = signal<'readable' | 'json'>('readable');
  protected readonly all = signal(false);
  protected readonly stats = computed(() => frequencyFileStats(this.file()));
  protected readonly range = computed(() => `${formatMHz(this.stats().minHz)} → ${formatMHz(this.stats().maxHz)}`);
  protected readonly pretty = computed(() => prettyFrequencyFile(this.file()));
  protected readonly deg = formatThetaDegrees;
  protected readonly delta = formatDelta;
  protected readonly colour = antennaColour;

  protected ignored(i: number): boolean {
    const n = this.antennaCount();
    return n != null && i >= n;
  }

  protected deltaSummary(t: (k: string, p?: Record<string, unknown>) => string): string {
    const n = this.antennaCount();
    const nd = this.stats().deltaCount;
    if (n == null) return String(nd);
    return t('siteConfiguration.frequency.deltaUsedIgnored', { used: Math.min(n, nd), ignored: Math.max(0, nd - n) });
  }

  protected download(): void {
    downloadText(this.fileName(), prettyFrequencyFile(this.file()));
  }
}

/** Aperçu d'un calib : lignes, colonnes (colonne i = antenne Ai), cellules lisibles ou texte. */
@Component({
  selector: 'lib-calib-dialog',
  standalone: true,
  imports: [
    TranslocoModule,
    WhButtonComponent,
    WhSegmentedComponent,
    FrequencyModalComponent,
    KvComponent,
    AntennaTagComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lib-frequency-modal
      *transloco="let t"
      [title]="t('siteConfiguration.frequency.calibDialogTitle', { name: fileName() })"
      widthClass="max-w-[1000px]"
      (closed)="closed.emit()">
      <div class="grid grid-cols-3 gap-3">
        <lib-kv [label]="t('siteConfiguration.frequency.calibRows')" [value]="rows()" />
        <lib-kv [label]="t('siteConfiguration.frequency.calibColumns')" [value]="columns()" />
        <lib-kv label="Format" [value]="t('siteConfiguration.frequency.calibFormatShort')" />
      </div>
      <div
        class="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
        <i class="pi pi-info-circle mt-0.5 text-xs"></i>{{ t('siteConfiguration.frequency.calibFormat') }}
      </div>
      @if (raw(); as text) {
        <div class="flex items-center">
          <wh-segmented
            class="ml-auto"
            size="sm"
            [scroll]="false"
            [options]="[
              { value: 'readable', label: t('siteConfiguration.frequency.readable') },
              { value: 'text', label: t('siteConfiguration.frequency.text') },
            ]"
            [(value)]="view" />
        </div>
        <div
          class="max-h-[44vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-slate-700 dark:bg-slate-900/40">
          @if (view() === 'readable') {
            <table class="w-full text-[11px] tabular-nums">
              <thead>
                <tr>
                  <th class="px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    {{ t('siteConfiguration.frequency.line') }}
                  </th>
                  @for (c of columnIndexes(); track c) {
                    <th class="px-2 py-1 text-left"><lib-antenna-tag [index]="c" /></th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of cells(); track $index) {
                  <tr class="border-t border-gray-100 dark:border-slate-700">
                    <td class="px-2 py-0.5 text-gray-400">{{ $index + 1 }}</td>
                    @for (c of row; track $index) {
                      <td class="whitespace-nowrap px-2 py-0.5 text-gray-700 dark:text-slate-200">
                        {{ c }}
                        @if (real(c)) {
                          <span class="text-[10px] text-gray-400">(+0i)</span>
                        }
                      </td>
                    }
                  </tr>
                }
                <tr>
                  <td [attr.colspan]="columns() + 1" class="px-2 py-0.5 text-gray-400">
                    {{ t('siteConfiguration.frequency.totalLines', { count: rows() }) }}
                  </td>
                </tr>
              </tbody>
            </table>
          } @else {
            <pre class="font-mono text-[11px] leading-5 text-gray-800 dark:text-slate-200">{{ text }}</pre>
          }
        </div>
      } @else {
        <p class="text-xs text-gray-500 dark:text-slate-400">{{ t('siteConfiguration.frequency.calibUnavailable') }}</p>
      }
      <ng-container slot="footer">
        @if (raw()) {
          <wh-button variant="outline" size="xs" userClass="text-blue-600 dark:text-blue-400" (click)="download()">
            <i class="pi pi-cloud-download mr-1 text-xs"></i>{{ t('siteConfiguration.files.download') }}
          </wh-button>
        }
        <wh-button variant="outline" size="xs" (click)="closed.emit()">{{
          t('siteConfiguration.files.close')
        }}</wh-button>
      </ng-container>
    </lib-frequency-modal>
  `,
})
export class CalibDialogComponent {
  readonly fileName = input.required<string>();
  readonly raw = input<string | null>(null);
  readonly rows = input.required<number>();
  readonly columns = input.required<number>();
  readonly closed = output<void>();
  protected readonly view = signal<'readable' | 'text'>('readable');
  protected readonly cells = computed(() => calibCells(this.raw() ?? '', 12));
  protected readonly columnIndexes = computed(() => Array.from({ length: this.columns() }, (_, i) => i));
  protected readonly real = isRealCalibCell;

  protected download(): void {
    downloadText(this.fileName(), this.raw() ?? '', 'text/plain');
  }
}

/** Aperçu d'un signal.json généré (une gateway) : quelle antenne remplit chaque DAC, export sans amp_div. */
@Component({
  selector: 'lib-signal-json-dialog',
  standalone: true,
  imports: [
    TranslocoModule,
    WhButtonComponent,
    WhSegmentedComponent,
    FrequencyModalComponent,
    AntennaTagComponent,
    FrequencyEntriesComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lib-frequency-modal
      *transloco="let t"
      [title]="
        t('siteConfiguration.frequency.signalDialogTitle', {
          gw: 'GW' + signal().gatewayIndex,
          name: signal().gatewayName,
        })
      "
      (closed)="closed.emit()">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-slate-400">
        <span
          >size <b class="text-gray-800 dark:text-slate-200">{{ signal().signal.size }}</b>
          @if (sizeLabel()) {
            ({{ sizeLabel() }})
          }
        </span>
        <span>{{ t('siteConfiguration.frequency.entries', { count: signal().entries }) }}</span>
        <wh-segmented
          class="ml-auto"
          size="sm"
          [scroll]="false"
          [options]="[
            { value: 'readable', label: t('siteConfiguration.frequency.readable') },
            { value: 'json', label: t('siteConfiguration.frequency.exportedJson') },
          ]"
          [(value)]="view" />
      </div>
      <div
        class="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
        <i class="pi pi-info-circle mt-0.5 text-xs"></i>{{ t('siteConfiguration.frequency.signalDialogInfo') }}
      </div>
      <div
        class="max-h-[50vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
        @if (view() === 'readable') {
          @for (d of dacs; track d) {
            <div
              class="mb-2 rounded-xl border border-gray-200 bg-white p-3 last:mb-0 dark:border-slate-700 dark:bg-slate-800">
              <div class="mb-2 flex flex-wrap items-center gap-2">
                <span class="text-sm font-semibold text-gray-900 dark:text-slate-100">RF OUT {{ d }}</span>
                <span
                  class="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-600 dark:bg-slate-700 dark:text-slate-300"
                  >dac{{ d }} · {{ entriesOf(d).length }} freq</span
                >
                @if (entriesOf(d).length && signal().dacAntenna[d] !== null) {
                  <lib-antenna-tag [index]="signal().dacAntenna[d]!" />
                }
              </div>
              @if (entriesOf(d).length) {
                <lib-frequency-entries [freq]="freqOf(d)" [theta]="thetaOf(d)" [limit]="12" />
              } @else {
                <div
                  class="rounded-lg border border-dashed border-gray-300 py-3 text-center text-xs text-gray-400 dark:border-slate-600 dark:text-slate-500">
                  {{ t('siteConfiguration.frequency.noFrequencyOnOutput') }}
                </div>
              }
            </div>
          }
        } @else {
          <pre class="font-mono text-[12px] leading-5 text-gray-800 dark:text-slate-200">{{ pretty() }}</pre>
        }
      </div>
      <ng-container slot="footer">
        <wh-button
          variant="outline"
          size="xs"
          userClass="text-blue-600 dark:text-blue-400"
          [disabled]="signal().conflicts.length > 0"
          (click)="download()">
          <i class="pi pi-cloud-download mr-1 text-xs"></i>{{ t('siteConfiguration.frequency.export') }}
        </wh-button>
        <wh-button variant="outline" size="xs" (click)="closed.emit()">{{
          t('siteConfiguration.files.close')
        }}</wh-button>
      </ng-container>
    </lib-frequency-modal>
  `,
})
export class SignalJsonDialogComponent {
  readonly signal = input.required<GeneratedSignalView>();
  readonly sizeLabel = input<string>('');
  readonly closed = output<void>();
  protected readonly dacs = [0, 1, 2, 3] as const;
  protected readonly view = signal<'readable' | 'json'>('readable');
  protected readonly pretty = computed(() => prettySignalJson(this.signal().signal, 20));

  protected entriesOf(d: number): Array<[number, number]> {
    const s = this.signal().signal;
    return [s.dac0, s.dac1, s.dac2, s.dac3][d];
  }
  protected freqOf(d: number): number[] {
    return this.entriesOf(d).map(e => e[0]);
  }
  protected thetaOf(d: number): number[] {
    return this.entriesOf(d).map(e => e[1]);
  }
  protected download(): void {
    downloadText(this.signal().fileName, signalJsonText(this.signal().signal));
  }
}

/** Import d'un fichier (fréquences ou calib) : dépôt ou sélection, contrôlé par le serveur. */
@Component({
  selector: 'lib-frequency-import-dialog',
  standalone: true,
  imports: [TranslocoModule, WhButtonComponent, WhDropzoneComponent, FrequencyModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lib-frequency-modal
      *transloco="let t"
      [title]="
        t(
          kind() === 'calib' ? 'siteConfiguration.frequency.importCalib' : 'siteConfiguration.frequency.importFrequency'
        )
      "
      widthClass="max-w-[640px]"
      (closed)="closed.emit()">
      <wh-dropzone [accept]="kind() === 'calib' ? '.txt,.csv' : '.json,.txt'" (fileSelected)="pick($event)">
        {{ t('siteConfiguration.frequency.dropHere') }}
        <span slot="hint">{{
          t(kind() === 'calib' ? 'siteConfiguration.frequency.calibFormat' : 'siteConfiguration.frequency.jsonFormat')
        }}</span>
      </wh-dropzone>
      @if (context()) {
        <div
          class="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
          <i class="pi pi-info-circle mt-0.5 text-xs"></i>{{ context() }}
        </div>
      }
      <ng-container slot="footer">
        <wh-button variant="outline" size="sm" (click)="closed.emit()">{{ t('siteConfiguration.cancel') }}</wh-button>
      </ng-container>
    </lib-frequency-modal>
  `,
})
export class FrequencyImportDialogComponent {
  readonly kind = input.required<'frequency' | 'calib'>();
  /** Ex. « Contrôlé pour LYON-07 (6 antennes). Fichier refusé : le fichier en place est conservé. » */
  readonly context = input<string>('');
  readonly picked = output<File>();
  readonly closed = output<void>();

  protected pick(file: File | null): void {
    if (file) this.picked.emit(file);
  }
}
